import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { FaArrowTrendDown } from "react-icons/fa6";
import { GrConfigure } from 'react-icons/gr';
import './DepreciationNode.css'; // Import the CSS file
import DepreciationConfigModal from './DepreciationConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { notifyError } from '../../../errors/notifyError';

const DepreciationNode = ({ id, data, selected }) => {
  const { BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm, depRateForITWDV, addnDepRate, taxRate, gstInput, gstPerc, name } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const nodes = useNodes();
  const { session, addDepreciationNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`DepreciationNode ${id} received data:`, data);
    // Log specific depreciation parameters for debugging
    console.log(`DepreciationNode ${id} depreciation parameters:`, {
      BasicPriceWind,
      BasicPriceSolar,
      BasicPriceSolarBtm,
      depRateForITWDV,
      addnDepRate,
      taxRate,
      name,
      gstInput,
      gstPerc
    });
  }, [id, data, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm, depRateForITWDV, addnDepRate, taxRate, gstInput, gstPerc, name]);

  // Check if node is configured properly
  // Either explicitly marked as configured or has all required parameters AND a name
  const isConfigured = data.isConfigured || (!!name && BasicPriceWind !== undefined && BasicPriceSolar !== undefined && BasicPriceSolarBtm !== undefined && taxRate !== undefined);
  
  // Log node state for debugging
  useEffect(() => {
    console.log(`DepreciationNode ${id} state:`, {
      isConfigured,
      name,
      BasicPriceWind,
      BasicPriceSolar,
      BasicPriceSolarBtm,
      depRateForITWDV,
      addnDepRate,
      taxRate,
      dataIsConfigured: data.isConfigured,
      dataIsApiConfigured: data.isApiConfigured
    });
  }, [id, isConfigured, name, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm, depRateForITWDV, addnDepRate, taxRate, gstInput, data]);

  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Log the current state for debugging
    console.log("openConfigModal called with state:", {
      isConfigured,
      BasicPriceWind,
      BasicPriceSolar,
      BasicPriceSolarBtm,
      depRateForITWDV,
      addnDepRate,
      taxRate,
      isModalOpen
    });
    
    // Open the configuration modal
    console.log(`Opening modal for ${isConfigured ? 'editing' : 'configuration'}`);
    
    // Force a small delay to ensure React state updates properly
    setTimeout(() => {
      setIsModalOpen(true);
      console.log("Modal should be open now:", { isModalOpen: true });
    }, 50);
  }, [isConfigured, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm, depRateForITWDV, addnDepRate, taxRate, isModalOpen]);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleConfigured = useCallback(async (configData) => {
    // Start the configuration process
    setIsConfiguring(true);
    
    try {
      const { name, BasicPriceWind, BasicPriceSolar, BasicPriceSolarBtm, depRateForITWDV, addnDepRate, taxRate, gstInput, gstPerc } = configData;
      const position = getNodePosition();
      
      // Log the configuration data for debugging
      console.log(`Configuring depreciation node ${id} -> ${name}:`, {
        configData,
        position,
        isConfigured: data.isConfigured,
        isApiConfigured: data.isApiConfigured
      });
      
      // First update the node ID to name mapping before adding to session
      updateNodeMapping(id, name);
      console.log(`Updated node mapping: ${id} -> ${name}`);
      
      // Add the depreciation node to the session state
      addDepreciationNode(
        name,
        BasicPriceWind,
        BasicPriceSolar,
        BasicPriceSolarBtm,
        depRateForITWDV,
        addnDepRate,
        taxRate,
        { x: position.x, y: position.y },
        gstInput,
        gstPerc
      );
      
      // Update the node data with the new configuration
      reactFlowInstance.setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id === id) {
            return {
              ...node,
              data: {
                ...node.data,
                name,
                BasicPriceWind,
                BasicPriceSolar,
                BasicPriceSolarBtm,
                depRateForITWDV,
                addnDepRate,
                taxRate,
                gstInput,
                isConfigured: true,
                isApiConfigured: true,
                gstPerc
              }
            };
          }
          return node;
        })
      );
      
      // If the node was previously selected, update the selection with the new name
      if (session.selected_node === id) {
        console.log(`Node was selected, updating selection from ${id} to ${name}`);
        updateSelectedNode(name);
      }
    } catch (error) {
      console.error('Error configuring depreciation node:', error);
      notifyError(error);
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, reactFlowInstance, getNodePosition, closeConfigModal, updateNodeMapping, addDepreciationNode, session, updateSelectedNode, data]);

  return (
    <>
      <div
        className={`depreciation-node ${selected ? 'depreciation-selected' : isConfigured ? 'depreciation-configured' : 'depreciation-not-configured'}`}
      >
        <div className={`depreciation-node-header ${isConfigured ? 'depreciation-configured' : 'depreciation-not-configured'}`}>
          <div className="depreciation-node-header-title">
            <div className="depreciation-node-type">Depreciation</div>
            {name && isConfigured && <div className="depreciation-node-name-display">{name}</div>}
          </div>
          <div className="depreciation-node-header-icons">
            {!isConfigured && (
              <button 
                onClick={openConfigModal}
                className="depreciation-config-button"
                title="Configure Depreciation Node"
                disabled={isConfiguring}
              >
                <ExclamationTriangleIcon className="depreciation-icon" />
              </button>
            )}
            {isConfigured && (
              <button 
                onClick={openConfigModal}
                className="depreciation-config-button"
                title="Edit Depreciation Parameters"
                disabled={isConfiguring}
              >
                <Pencil1Icon className="depreciation-icon" />
              </button>
            )}
            <FaArrowTrendDown className="depreciation-icon" />
          </div>
        </div>
        <div className="depreciation-node-content">
          <div className="depreciation-text-xs">
            {!isConfigured && (
              <div className="depreciation-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="depreciation-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                <div>BP Wind: ₹{BasicPriceWind} Cr</div>
                <div>BP Solar: ₹{BasicPriceSolar} Cr</div>
                <div>BP Solar (BTM): ₹{BasicPriceSolarBtm} Cr</div>
                <div>Dep Rate (IT WDV): {depRateForITWDV}%</div>
                <div>Addn Dep Rate: {addnDepRate}%</div>
                <div>Tax Rate: {taxRate}%</div>
                {gstPerc != null && gstPerc !== '' && (<div>GST: {gstPerc}%</div>)}
              </>
            )}
          </div>
          <div className="depreciation-flex-container">
            {/* Only output handle, no input handle */}
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`depreciation-connection-point ${isConfigured ? 'depreciation-configured-output' : 'depreciation-not-configured'}`}
              isConnectable={isConfigured} // Only allow connections if the node is configured
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <DepreciationConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{ 
          name, 
          BasicPriceWind,
          BasicPriceSolar,
          BasicPriceSolarBtm,
          depRateForITWDV,
          addnDepRate,
          taxRate,
          isConfigured: data.isConfigured,
          position: getNodePosition(),
          gstInput,
          gstPerc
        }}
      />
    </>
  );
};

export default DepreciationNode;
